/**
 * Sticky CTA Bar (Task 11)
 * Slides up from bottom after scrolling 300px past hero section.
 * Hides when user scrolls back up past the hero.
 * Only for LP variant pages.
 */
import React, { useState, useEffect } from "react";
import { ArrowRight, X } from "lucide-react";

export default function StickyCTABar({ heroRef, onCTAClick }) {
  const [pastHero, setPastHero] = useState(false);
  const [ctaOnScreen, setCtaOnScreen] = useState(true); // stay hidden until we know
  const [belowCtaZone, setBelowCtaZone] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // Only eligible to show once the user has scrolled past the hero.
  useEffect(() => {
    const onScroll = () => {
      const past = !heroRef?.current
        ? window.scrollY > 500
        : window.scrollY > heroRef.current.getBoundingClientRect().bottom + window.scrollY + 300;
      setPastHero(past);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [heroRef]);

  // Hide whenever a primary on-page CTA is in view (.ap-btn-fire.ap-btn-lg — the
  // hero / pricing / final buttons; the bar's own button is ap-btn-md, excluded).
  // Also hide while the user is AT/BELOW the pricing zone: once a below-hero CTA
  // has been scrolled PAST (sits above the viewport), the remaining gaps
  // (pricing → FAQ → final CTA) are too short — the bar would just flicker. This
  // is position-based, not a one-way latch: scroll back up above the pricing card
  // and the bar returns for the long CTA-less middle stretch. Hero CTAs are
  // excluded — past-the-hero is exactly where the bar should serve.
  useEffect(() => {
    const ctas = Array.from(document.querySelectorAll(".ap-btn-fire.ap-btn-lg"));
    if (!ctas.length) { setCtaOnScreen(false); return; }
    const hero = heroRef?.current;
    const onScreen = new Set();
    const passedAbove = new Set(); // below-hero CTAs the user has scrolled past
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          onScreen.add(e.target);
          passedAbove.delete(e.target);
        } else {
          onScreen.delete(e.target);
          const isHeroCta = hero ? hero.contains(e.target) : false;
          if (!isHeroCta && e.boundingClientRect.bottom < 0) passedAbove.add(e.target);
          else passedAbove.delete(e.target);
        }
      });
      setCtaOnScreen(onScreen.size > 0);
      setBelowCtaZone(passedAbove.size > 0);
    }, { threshold: 0 });
    ctas.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [heroRef]);

  // Always mounted so it can slide OUT as well as in — visibility is driven by a
  // transform transition rather than mount/unmount (which would vanish instantly).
  const show = pastHero && !ctaOnScreen && !belowCtaZone && !dismissed;

  return (
    <div
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${show ? "translate-y-0" : "translate-y-full pointer-events-none"}`}
      aria-hidden={!show}
    >
      <div className="bg-[var(--ap-ink)] border-t border-white/10 shadow-2xl">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-2 h-2 bg-[var(--ap-green)] rounded-full flex-shrink-0 animate-pulse" />
            <span className="text-white text-sm font-medium truncate">
              Start My Free ATS Analysis
              <span className="hidden sm:inline text-white/60 font-normal">
                {" "}→ Free · No credit card · 2 min
              </span>
            </span>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onCTAClick}
              className="ap-btn ap-btn-fire ap-btn-md whitespace-nowrap"
            >
              Get My Free Score
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 text-white/60 hover:text-white transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
